import * as React from "react";

import styles from "./ColumnResizeHandle.module.css";

interface ColumnResizeHandleProps {
  // Pointer positions, not widths. Turning an x-coordinate into a column width
  // needs the width the drag began at and the widths of every other column, and
  // keeping that arithmetic in one reducer is what stops the handle and the
  // panel disagreeing about the outcome of a gesture.
  onResizeStart: (clientX: number) => void;
  onResizeMove: (clientX: number) => void;
  onResizeEnd: () => void;
  // The gesture was taken away rather than finished: an OS interruption, or
  // capture yanked because the element went away.
  onResizeAbort: () => void;
  ariaLabel: string;
}

export function ColumnResizeHandle({
  onResizeStart,
  onResizeMove,
  onResizeEnd,
  onResizeAbort,
  ariaLabel,
}: ColumnResizeHandleProps) {
  // Which pointer this handle is following. A ref rather than state because
  // pointermove fires at display rate and none of this affects what is drawn.
  const pointerIdRef = React.useRef<number | null>(null);

  const handlePointerDown = (e: React.PointerEvent<HTMLSpanElement>) => {
    // Don't initiate drag for right-click / middle-click.
    if (e.button !== 0) {
      return;
    }
    pointerIdRef.current = e.pointerId;
    // Capture so the drag keeps receiving moves once the pointer leaves the
    // handle, which it does immediately on any real drag.
    e.currentTarget.setPointerCapture(e.pointerId);
    onResizeStart(e.clientX);
    e.preventDefault();
    e.stopPropagation();
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLSpanElement>) => {
    if (pointerIdRef.current !== e.pointerId) {
      return;
    }
    onResizeMove(e.clientX);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLSpanElement>) => {
    if (pointerIdRef.current !== e.pointerId) {
      return;
    }
    pointerIdRef.current = null;
    onResizeEnd();
  };

  // pointercancel fires when the user agent decides the pointer won't produce
  // more events; lostpointercapture fires when capture is yanked. A clean
  // pointerup also fires lostpointercapture afterwards, but by then the pointer
  // id is cleared, so this no-ops.
  const handlePointerAbort = (e: React.PointerEvent<HTMLSpanElement>) => {
    if (pointerIdRef.current !== e.pointerId) {
      return;
    }
    pointerIdRef.current = null;
    onResizeAbort();
  };

  return (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      className={styles.handle}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerAbort}
      onLostPointerCapture={handlePointerAbort}
      // Stop the synthetic click that follows pointerup from reaching the
      // header (which, on dynamic columns, toggles the filter popover).
      onClick={(e) => e.stopPropagation()}
    />
  );
}
